import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import copy from "clipboard-copy";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Copy, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Mask } from "@/utils/mask";
import {
  publicShowPaymentAdvance,
  publicCheckPaymentAdvanceStatus,
} from "@/Api/paymentAdvances";
import { PAYMENT_ADVANCE_STATUS } from "@/utils/constants";

interface PaymentAdvanceData {
  id: number;
  uuid: string;
  amount: number;
  status: string;
  pix_qrcode?: string;
  pix_txid?: string;
  paid_at?: string;
  client?: {
    id: number;
    name: string;
  };
  seller?: {
    id: number;
    user?: {
      name: string;
    };
  };
}

export function PublicPaymentAdvancePage() {
  const { uuid } = useParams<{ uuid: string }>();
  const [paymentAdvance, setPaymentAdvance] =
    useState<PaymentAdvanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPaymentAdvance = useCallback(async () => {
    if (!uuid) {
      setError("UUID do Pix Gerado não fornecido");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, ok } = await publicShowPaymentAdvance(uuid);

      if (!ok) {
        setError("Pix Gerado não encontrado ou não disponível");
        return;
      }

      setPaymentAdvance(data?.response);
    } catch (err) {
      setError("Erro ao carregar dados do Pix Gerado");
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  const checkPaymentStatus = useCallback(async () => {
    if (!paymentAdvance || checking || !uuid) return;

    try {
      setChecking(true);
      const { data, ok } = await publicCheckPaymentAdvanceStatus(uuid);

      if (!ok) return;

      const response = data?.response;

      if (response?.status === PAYMENT_ADVANCE_STATUS.PAID) {
        setPaymentAdvance((prev) =>
          prev
            ? {
                ...prev,
                status: PAYMENT_ADVANCE_STATUS.PAID,
                paid_at: response.paid_at,
              }
            : null
        );
        toast.success("Pagamento confirmado!");
      }
    } catch (err) {
      toast.error("Erro ao verificar status do pagamento");
    } finally {
      setChecking(false);
    }
  }, [paymentAdvance, checking, uuid]);

  const handleCopyPix = async (pixCode: string) => {
    try {
      await copy(pixCode);
      setCopied(true);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error("Erro ao copiar código PIX");
    }
  };

  useEffect(() => {
    fetchPaymentAdvance();
  }, [fetchPaymentAdvance]);

  useEffect(() => {
    if (!paymentAdvance) return;

    const isPending = paymentAdvance.status === PAYMENT_ADVANCE_STATUS.PENDING;

    if (!isPending) return;

    const interval = setInterval(checkPaymentStatus, 10000);
    return () => clearInterval(interval);
  }, [paymentAdvance, checkPaymentStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
          <p className="text-gray-600">Carregando dados do pagamento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Erro</h2>
              <p className="text-gray-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!paymentAdvance) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <AlertCircle className="w-12 h-12 text-gray-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">
                Pix Gerado não encontrado
              </h2>
              <p className="text-gray-600">
                O Pix Gerado solicitado não foi encontrado.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPaid =
    paymentAdvance.status === PAYMENT_ADVANCE_STATUS.PAID ||
    paymentAdvance.status === PAYMENT_ADVANCE_STATUS.USED;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto space-y-6">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Pagamento PIX Antecipado</CardTitle>
            <p className="text-sm text-gray-600">
              Pix Gerado #{paymentAdvance.id}
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {paymentAdvance.client && (
                <div className="text-center">
                  <p className="text-sm text-gray-600">Cliente:</p>
                  <p className="font-medium">{paymentAdvance.client.name}</p>
                </div>
              )}

              <div className="text-center">
                <p className="text-sm text-gray-600">Valor:</p>
                <p className="text-2xl font-bold text-green-600">
                  {Mask.formatCurrency(paymentAdvance.amount)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {paymentAdvance.pix_qrcode ? (
          <div className="flex flex-col items-center p-6 bg-white dark:bg-zinc-800 rounded-lg shadow-sm">
            <h3 className="text-lg font-semibold mb-4">Pagamento PIX</h3>

            {isPaid ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="bg-green-100 p-4 rounded-full mb-4">
                  <CheckCircle2 className="w-16 h-16 text-green-600" />
                </div>
                <h4 className="text-xl font-bold text-green-600 mb-2">
                  Pagamento Confirmado!
                </h4>
                <p className="text-center text-gray-600 mb-4">
                  O pagamento de{" "}
                  {Mask.formatCurrency(paymentAdvance.amount)} foi confirmado
                  com sucesso.
                </p>
                {paymentAdvance.paid_at && (
                  <p className="text-sm text-gray-500">
                    Pago em:{" "}
                    {new Date(paymentAdvance.paid_at).toLocaleString("pt-BR")}
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="bg-white p-4 rounded-lg mb-4">
                  <QRCode
                    value={paymentAdvance.pix_qrcode}
                    size={192}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  />
                </div>

                <div className="w-full max-w-xs mb-4">
                  <p className="text-sm text-center mb-2">Valor a pagar:</p>
                  <p className="text-2xl font-bold text-center text-green-600 mb-4">
                    {Mask.formatCurrency(paymentAdvance.amount)}
                  </p>

                  <div className="flex items-center gap-2 p-2 bg-gray-100 dark:bg-zinc-700 rounded-md">
                    <p className="text-xs flex-1 truncate">
                      {paymentAdvance.pix_qrcode}
                    </p>
                    <Button
                      size="icon"
                      onClick={() =>
                        handleCopyPix(paymentAdvance.pix_qrcode || "")
                      }
                      className="h-8 w-8 shrink-0"
                    >
                      {copied ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="w-full max-w-xs space-y-2">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={checkPaymentStatus}
                      disabled={checking}
                    >
                      {checking ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verificando...
                        </>
                      ) : (
                        "Verificar pagamento"
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-center text-muted-foreground">
                    O status do pagamento é verificado automaticamente a cada 10
                    segundos.
                  </p>
                </div>
              </>
            )}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center">
                <AlertCircle className="w-12 h-12 text-orange-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  PIX não disponível
                </h3>
                <p className="text-gray-600">
                  Este Pix Gerado não possui código PIX disponível para
                  visualização.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
